import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FaChevronLeft } from "react-icons/fa6";
import { colors, font, radius, shadow } from "../theme";
import { useWindowManager } from "../WindowManager";
import AppIcon from "../AppIcon";

const Root = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  font-family: ${font.body};
  background: linear-gradient(
    170deg,
    ${colors.wallpaperTop} 0%,
    ${colors.desktop} 50%,
    ${colors.wallpaperBottom} 100%
  );
  display: flex;
  flex-direction: column;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px 4px;
  font-size: 13px;
  font-weight: 700;
  color: ${colors.ink};
`;

const Hero = styled.div`
  padding: 18px 22px 6px;
`;

const Hi = styled.div`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${colors.inkFaint};
`;

const Name = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: ${colors.green};
`;

const Sub = styled.div`
  font-size: 14px;
  color: ${colors.inkSoft};
  margin-top: 4px;
`;

const Grid = styled.div`
  flex: 1;
  align-content: flex-start;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px 10px;
  padding: 22px;
  overflow-y: auto;

  @media (max-width: 360px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AppButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  animation: appLaunch 0.3s ease both;
`;

const Tile = styled.div<{ $accent: string }>`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  border: 2px solid ${colors.ink};
  background: ${({ $accent }) => $accent};
  box-shadow: ${shadow.icon};
  color: ${colors.ink};

  ${AppButton}:active & {
    transform: scale(0.92);
  }
`;

const TileLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.ink};
  text-align: center;
`;

const Fullscreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  background: ${colors.window};
  animation: appLaunch 0.22s ease both;
`;

const AppHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  padding: 14px 16px;
  border-bottom: 2px solid ${colors.ink};
  background: ${colors.windowHeader};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 2px solid ${colors.ink};
  background: #fff;
  border-radius: ${radius.pill};
  padding: 6px 14px 6px 10px;
  font-family: ${font.body};
  font-size: 14px;
  font-weight: 700;
  color: ${colors.ink};
  cursor: pointer;
`;

const AppTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const AppBody = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;

const PhoneShell = () => {
  const { registry, windows, focusedId, getApp, openApp, closeWindow } =
    useWindowManager();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 15);
    return () => clearInterval(id);
  }, []);

  const apps = registry.filter((a) => a.desktop);

  const visible = windows.filter((w) => !w.minimized);
  const active =
    visible.find((w) => w.id === focusedId) ??
    visible.sort((a, b) => b.z - a.z)[0];
  const activeApp = active ? getApp(active.appId) : undefined;

  const time = now.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <Root>
      <StatusBar>
        <span>{time}</span>
        <span>Harrison OS</span>
      </StatusBar>
      <Hero>
        <Hi>Hey, I'm</Hi>
        <Name>Harrison Qu</Name>
        <Sub>Full-stack web &amp; mobile developer - tap an app to explore.</Sub>
      </Hero>
      <Grid>
        {apps.map((app) => (
          <AppButton key={app.id} onClick={() => openApp(app.id)}>
            <Tile $accent={app.accent}>
              <AppIcon
                icon={app.icon}
                size={28}
                color={app.accent === colors.ink ? "#fff" : colors.ink}
              />
            </Tile>
            <TileLabel>{app.title}</TileLabel>
          </AppButton>
        ))}
      </Grid>

      {active && activeApp && (
        <Fullscreen>
          <AppHeader>
            <BackButton onClick={() => closeWindow(active.id)}>
              <AppIcon icon={FaChevronLeft} size={13} /> Home
            </BackButton>
            <AppTitle>{activeApp.title}</AppTitle>
          </AppHeader>
          <AppBody>
            <activeApp.component />
          </AppBody>
        </Fullscreen>
      )}
    </Root>
  );
};

export default PhoneShell;
